export type Language = 'en' | 'id' | 'ko';

/**
 * Pure JavaScript language manager that syncs with localStorage
 * and provides language state without React dependencies.
 */
class LanguageManager {
    private currentLanguage: Language = 'en';
    private listeners: Set<(lang: Language) => void> = new Set();

    constructor() {
        // Initialize from localStorage
        const saved = localStorage.getItem('language');
        if (saved && ['en', 'id', 'ko'].includes(saved)) {
            this.currentLanguage = saved as Language;
        }
    }

    /**
     * Get current language
     */
    getLanguage(): Language {
        return this.currentLanguage;
    }

    /**
     * Set language and notify all listeners
     */
    setLanguage(lang: Language): void {
        if (this.currentLanguage !== lang) {
            this.currentLanguage = lang;
            localStorage.setItem('language', lang);
            this.notifyListeners();
        }
    }

    /**
     * Subscribe to language changes
     * Returns unsubscribe function
     */
    subscribe(listener: (lang: Language) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener(this.currentLanguage));
    }
}

// Single instance shared across the app
export const languageManager = new LanguageManager();